/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import type React from 'react';
import { useCallback } from 'react';

import { OutlinedButton } from '../outlined-button';
import { TextButton } from '../text-button';
import { Spotlight, type SpotlightCloseAction } from './Spotlight';
import { useSpotlightContext } from './SpotlightProvider';

type SpotlightTourAction =
  | SpotlightCloseAction
  | 'dismiss'
  | 'back'
  | 'next'
  | 'close'
  | 'open';

type SpotlightTourProps = {
  /** An array of `Spotlight` component props objects. SpotlightTour overrides the following props, to simplify your implementation, if they are undefined: `actions`, `beforeActions`, `initialFocus`, `onClose`. If you want custom behaviour for a certain `Spotlight` you can set these props yourself. */
  spotlights: React.ComponentProps<typeof Spotlight>[];
  /** A string that will be the text for the dismiss button */
  dismissButtonLabel?: string;
  /** A string that will be the text for the back button */
  backButtonLabel?: string;
  /** A string that will be the text for the next button */
  nextButtonLabel?: string;
  /** A string that will be the text for the close button */
  closeButtonLabel?: string;
  /** A callback function that is called when some actions are performed inside a `Spotlight`. It receives two arguments: `target` (string identifying the `Spotlight` that the action was performed in) and `action` (string describing the action that has been performed. These actions can be "dismiss", "back", "next", "close", "clickOutside" or "escapeKeyDown") */
  onAction?: (target: string, action: SpotlightTourAction) => void;
};

export const SpotlightTour = ({
  spotlights,
  dismissButtonLabel = 'Dismiss',
  backButtonLabel = 'Back',
  nextButtonLabel = 'Next',
  closeButtonLabel = 'Close',
  onAction,
}: SpotlightTourProps) => {
  const { setActiveSpotlight, setIsOpen } = useSpotlightContext();

  const next = useCallback(
    (index: number) => {
      onAction?.(spotlights[index].target, 'next');
      setActiveSpotlight(spotlights[index + 1].target);
    },
    [onAction, spotlights, setActiveSpotlight],
  );

  const back = useCallback(
    (index: number) => {
      onAction?.(spotlights[index].target, 'back');
      setActiveSpotlight(spotlights[index - 1].target);
    },
    [onAction, spotlights, setActiveSpotlight],
  );

  const dismiss = useCallback(
    (index: number) => {
      onAction?.(spotlights[index].target, 'dismiss');
      setActiveSpotlight(null);
      setIsOpen(false);
    },
    [onAction, spotlights, setActiveSpotlight, setIsOpen],
  );

  const close = useCallback(
    (index: number) => {
      onAction?.(spotlights[index].target, 'close');
      setActiveSpotlight(null);
      setIsOpen(false);
    },
    [onAction, spotlights, setActiveSpotlight, setIsOpen],
  );

  const getActions = useCallback(
    (index: number, actions: React.ReactNode): React.ReactNode => {
      if (actions !== undefined && actions !== null) {
        return actions;
      }

      const isFirst = index === 0;
      const isLast = index === spotlights.length - 1;
      const hasMultiple = spotlights.length > 1;

      const newActions: React.ReactNode[] = [];

      if (!isLast && hasMultiple) {
        newActions.push(
          <TextButton onClick={() => dismiss(index)}>
            {dismissButtonLabel}
          </TextButton>,
        );
      }

      if (!isFirst && hasMultiple) {
        newActions.push(
          <TextButton onClick={() => back(index)}>
            {backButtonLabel}
          </TextButton>,
        );
      }

      if (isLast) {
        newActions.push(
          <OutlinedButton onClick={() => close(index)}>
            {closeButtonLabel}
          </OutlinedButton>,
        );
      } else {
        newActions.push(
          <OutlinedButton onClick={() => next(index)}>
            {nextButtonLabel}
          </OutlinedButton>,
        );
      }

      return newActions;
    },
    [
      spotlights,
      dismissButtonLabel,
      dismiss,
      backButtonLabel,
      back,
      closeButtonLabel,
      close,
      nextButtonLabel,
      next,
    ],
  );

  const getBeforeActions: (
    index: number,
    beforeActions: React.ReactNode,
  ) => React.ReactNode = useCallback(
    (index: number, beforeActions: React.ReactNode) => {
      if (beforeActions !== undefined && beforeActions !== null) {
        return beforeActions;
      }

      return spotlights.length > 1 ? `${index + 1}/${spotlights.length}` : null;
    },
    [spotlights],
  );

  const getOnClose: (
    index: number,
    onClose: ((reason: SpotlightCloseAction) => void) | undefined,
  ) => ((reason: SpotlightCloseAction) => void) | undefined = useCallback(
    (
      index: number,
      onClose: ((reason: SpotlightCloseAction) => void) | undefined,
    ) => {
      if (onClose) {
        return onClose;
      }

      return (reason) => {
        onAction?.(spotlights[index].target, reason);
      };
    },
    [onAction, spotlights],
  );

  const getOnOpen: (
    index: number,
    onOpen: (() => void) | undefined,
  ) => (() => void) | undefined = useCallback(
    (index: number, onOpen: (() => void) | undefined) => {
      if (onOpen) {
        return onOpen;
      }

      return () => {
        onAction?.(spotlights[index].target, 'open');
      };
    },
    [onAction, spotlights],
  );

  return (
    <>
      {spotlights.map((spotlight, index) => {
        const {
          actions,
          beforeActions,
          initialFocus,
          onClose,
          onOpen,
          ...restSpotlight
        } = spotlight;
        const newActions = getActions(index, actions);
        const newBeforeActions = getBeforeActions(index, beforeActions);
        const newOnClose = getOnClose(index, onClose);
        const newOnOpen = getOnOpen(index, onOpen);

        return (
          <Spotlight
            key={index}
            {...restSpotlight}
            actions={newActions}
            beforeActions={newBeforeActions}
            initialFocus={initialFocus}
            onClose={newOnClose}
            onOpen={newOnOpen}
          />
        );
      })}
    </>
  );
};
